import { useEffect, useState } from 'react';
import { client } from '@/lib/api';
import type { document, plans } from '@/lib/client';
import { cn } from '@/lib/utils';
import { TRACE_POLL_MS } from '@/stores/traceStore';

/**
 * One document's or plan's pipeline, top to bottom (docs/plans/document-to-plan-pipeline.md, R4; after the
 * experiment's round diagram): a box per step in the order a run makes them, with its calls, cost and what it made;
 * grey where the step did not run this round. Reading, then the questions, then the planning, then each step of the
 * plan's run and its revert. Informative only: a read that fails keeps the last drawing.
 */

interface BoxDef {
  step: string;
  label: string;
}

/** The decomposer's steps, in the order a round makes them. */
const READ: BoxDef[] = [
  { step: 'answers', label: 'answers' },
  { step: 'units', label: 'units' },
  { step: 'propose', label: 'propose' },
  { step: 'ground', label: 'ground' },
  { step: 'parts', label: 'parts' },
  { step: 'ground_parts', label: 'ground the parts' },
  { step: 'critic', label: 'critic' },
  { step: 'ask', label: 'ask' },
  { step: 'group', label: 'group' },
  { step: 'suggest', label: 'suggest answers' },
];

/** The routing's steps. */
const PLAN: BoxDef[] = [
  { step: 'read_account', label: 'read the account' },
  { step: 'draft', label: 'draft the calls' },
  { step: 'plan_made', label: 'the plan' },
];

/** Steps a later round does not make again. */
const ROUND_ONE_ONLY = new Set(['units', 'propose']);

type Facts = plans.TraceFacts;

/** A box: its facts summed when the step ran more than once (the critic's rounds), the last detail kept. */
function summed(entries: readonly plans.TraceEntry[]): Map<string, Facts> {
  const out = new Map<string, Facts>();
  for (const e of entries) {
    if (e.step === null) continue;
    const was = out.get(e.step) ?? {};
    const f = e.facts ?? {};
    out.set(e.step, {
      calls: (was.calls ?? 0) + (f.calls ?? 0),
      usd: (was.usd ?? 0) + (f.usd ?? 0),
      detail: f.detail ?? was.detail,
      parts: f.parts ?? was.parts,
    });
  }
  return out;
}

const money = (usd: number): string => `$${usd.toFixed(3)}`;

function Arrow({ dashed = false }: { dashed?: boolean }) {
  return <div className={cn('mx-auto h-3 w-px', dashed ? 'border-l border-dashed border-muted-foreground/50' : 'bg-muted-foreground/40')} />;
}

function Box({ label, facts, state, note }: { label: string; facts?: Facts; state: 'done' | 'now' | 'grey'; note?: string }) {
  const meta = facts === undefined ? null : (facts.calls ?? 0) === 0 ? 'code only' : `${facts.calls} call${facts.calls === 1 ? '' : 's'} · ${money(facts.usd ?? 0)}`;
  return (
    <div className={cn('rounded-md border px-2.5 py-1.5 text-xs',
      state === 'grey' ? 'border-border/50 text-muted-foreground/50' : state === 'now' ? 'animate-pulse border-sky-400/60 bg-sky-400/5' : 'border-border bg-card')}>
      <div className="flex items-baseline gap-2">
        <span className={cn('font-medium', state === 'grey' ? '' : 'text-foreground')}>{label}</span>
        {meta && <span className="ml-auto text-[10px] text-muted-foreground">{meta}</span>}
      </div>
      {(facts?.detail || note) && <p className="text-[11px] text-muted-foreground">{facts?.detail || note}</p>}
      {facts?.parts && Object.keys(facts.parts).length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {Object.entries(facts.parts).map(([k, n]) => <span key={k} className="rounded border border-border px-1 text-[10px] text-muted-foreground">{k} <span className="text-foreground">{n}</span></span>)}
        </div>
      )}
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{title}</h4>
      {children}
    </div>
  );
}

/** The boxes of a list of steps, each lit by its facts; the first not yet lit pulses while the job runs. */
function Column({ defs, facts, running, grey }: { defs: BoxDef[]; facts: Map<string, Facts>; running: boolean; grey?: (step: string) => string | null }) {
  const next = running ? defs.find((d) => !facts.has(d.step) && (grey?.(d.step) ?? null) === null)?.step : undefined;
  return (
    <div>
      {defs.map((d, i) => {
        const why = grey?.(d.step) ?? null;
        const state = facts.has(d.step) ? 'done' : d.step === next ? 'now' : 'grey';
        return (
          <div key={d.step}>
            {i > 0 && <Arrow />}
            <Box label={d.label} facts={facts.get(d.step)} state={state} note={state === 'grey' ? why ?? (running ? undefined : 'not in this round') : undefined} />
          </div>
        );
      })}
    </div>
  );
}


/** The executor's stages for one step, in the order it goes through them. */
const STAGES_OF_A_STEP = ['read ids', 'look up', 'call', 'evaluate', 'check', 'outcome', 'undo'] as const;
type StepStage = (typeof STAGES_OF_A_STEP)[number];

interface Attempt {
  request?: { method?: string; path?: string };
  failure?: { status?: number | null; message?: string } | null;
  evaluation?: { verdict?: string; reason?: string } | null;
  first?: { request?: { method?: string; path?: string }; status?: number | null; ok?: boolean; error?: string };
}

const VERDICT: Record<string, string> = {
  retry: 'retry, corrected', first: 'another call first', already_done: 'already done', needs_scopes: 'needs scopes', other_channel: 'for the person',
};

/** What each executor stage of a step did, from its run; a stage it did not go through is absent. Pure. */
function stagesOf(step: plans.PlanStepView): Map<StepStage, string[]> {
  const run = step.run;
  const out = new Map<StepStage, string[]>();
  if (!run) return out;
  const r = run.response ?? {};
  const read = Array.isArray(r['read']) ? (r['read'] as Array<{ kind: string; name: string; id: string | null }>) : [];
  if (read.length > 0) out.set('read ids', read.map((x) => `${x.kind} "${x.name}" → ${x.id ?? 'not found'}`));
  if (r['looked_up'] === 'found' || r['looked_up'] === 'absent') out.set('look up', [r['looked_up'] === 'found' ? 'already on the account' : 'not on the account yet']);
  const req = run.request as { method?: string; path?: string } | undefined;
  if (req?.method) out.set('call', [`${req.method} ${req.path ?? ''}${r['status'] === undefined ? '' : ` → ${String(r['status'])}`}`]);
  else if (step.channel === 'human') out.set('call', ['by hand, in the Zoom web portal']);
  const attempts = Array.isArray(r['attempts']) ? (r['attempts'] as Attempt[]) : [];
  if (attempts.length > 0) {
    out.set('evaluate', attempts.flatMap((a) => [
      ...(a.failure ? [`Zoom refused${a.failure.status ? ` (${a.failure.status})` : ''}: ${(a.failure.message ?? '').replace(/^.*?failed: /, '').slice(0, 120)}`] : []),
      ...(a.evaluation?.verdict ? [`→ ${VERDICT[a.evaluation.verdict] ?? a.evaluation.verdict}: ${(a.evaluation.reason ?? '').slice(0, 140)}`] : a.failure ? [] : [`retried ${a.request?.method ?? ''} without a field`]),
      ...(a.first ? [`made first: ${a.first.request?.method ?? ''} ${a.first.request?.path ?? ''} → ${a.first.ok ? a.first.status ?? 'ok' : `failed${a.first.error ? `: ${a.first.error.slice(0, 80)}` : ''}`}`] : []),
    ]));
  }
  const v = run.verification;
  if (v) out.set('check', [v.skipped ? (v.skipped === 'dry_run' ? 'dry run: nothing to check' : v.skipped === 'no_read_scope' ? 'not checked: the app cannot read it back' : v.skipped === 'agent' ? 'the browser agent checked it on the page' : 'no check for this call') : v.passed ? `checked on the account${v.checks.length ? ` (${v.checks.length} fields)` : ''}` : `the check failed: ${v.checks.filter((c) => !c.passed).map((c) => c.field).join(', ')}`]);
  const handed = typeof r['handed_over'] === 'string' ? (r['handed_over'] as string) : null;
  const already = typeof r['already_done'] === 'string' ? (r['already_done'] as string) : null;
  out.set('outcome', [run.status.replace('_', ' '), ...(handed ? [handed.slice(0, 200)] : []), ...(already ? [`already done: ${already.slice(0, 160)}`] : []), ...(run.error?.message && !handed ? [run.error.message.slice(0, 200)] : [])]);
  if (run.revert_status) out.set('undo', [`${run.revert_status}${run.revert_action?.kind ? ` (${run.revert_action.kind})` : ''}`]);
  return out;
}

const RUN_TONE: Record<string, string> = {
  completed: 'border-emerald-500/40',
  failed: 'border-red-500/50',
  waiting_human: 'border-amber-500/50',
  running: 'animate-pulse border-sky-400/60',
};

/** One step's executor stages, top to bottom: those it went through lit, the others grey. */
function StepStages({ step }: { step: plans.PlanStepView }) {
  const stages = stagesOf(step);
  return (
    <div className="ml-3 mt-1 border-l border-border pl-2.5">
      {STAGES_OF_A_STEP.map((name, i) => {
        const lines = stages.get(name);
        return (
          <div key={name}>
            {i > 0 && <div className="ml-2 h-1.5 w-px bg-muted-foreground/30" />}
            <div className={cn('rounded border px-2 py-1 text-[11px]', lines ? 'border-border bg-card' : 'border-border/40 text-muted-foreground/40')}>
              <span className={cn('font-medium', lines ? 'text-foreground' : '')}>{name}</span>
              {lines?.map((line, j) => <p key={j} className="break-words text-muted-foreground">{line}</p>)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** A plan's run, a box a step, in order; a step opens to its executor stages. */
function RunColumn({ plan }: { plan: plans.PlanView }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div>
      {[...plan.steps].sort((a, b) => a.position - b.position).map((s, i) => {
        const run = s.run;
        const went = stagesOf(s);
        return (
          <div key={s.key}>
            {i > 0 && <Arrow />}
            <button
              onClick={() => setOpen(open === s.key ? null : s.key)}
              className={cn('w-full rounded-md border bg-card px-2.5 py-1.5 text-left text-xs', RUN_TONE[run?.status ?? ''] ?? 'border-border/50 text-muted-foreground/60')}
            >
              <div className="flex items-baseline gap-2">
                <span className="truncate font-medium text-foreground">{s.title}</span>
                <span className="ml-auto shrink-0 text-[10px] uppercase text-muted-foreground">{s.channel === 'human' ? 'you' : s.channel}</span>
              </div>
              <div className="mt-0.5 flex flex-wrap gap-1">
                {STAGES_OF_A_STEP.map((name) => (
                  <span key={name} className={cn('rounded px-1 text-[9px] uppercase', went.has(name) ? 'bg-secondary text-foreground' : 'text-muted-foreground/40')}>{name}</span>
                ))}
              </div>
            </button>
            {open === s.key && <StepStages step={s} />}
          </div>
        );
      })}
    </div>
  );
}

export function PipelineDiagram({ kind, id }: { kind: plans.TraceKind; id: string }) {
  const [steps, setSteps] = useState<plans.TraceEntry[]>([]);
  const [doc, setDoc] = useState<document.DocumentView | null>(null);
  const [plan, setPlan] = useState<plans.PlanView | null>(null);

  useEffect(() => {
    let alive = true;
    const read = async () => {
      try {
        const [{ steps: s }, d] = await Promise.all([
          client.plans.getTraceSteps(id),
          kind === 'document' ? client.plans.getDocument(id).then((r) => r.document) : Promise.resolve(null),
        ]);
        const planId = kind === 'plan' ? id : d?.plan_id ?? null;
        const p = planId === null ? null : (await client.plans.getPlan(planId)).plan;
        if (!alive) return;
        setSteps(s);
        setDoc(d);
        setPlan(p);
      } catch {
        // Informative: the last drawing stays.
      }
    };
    void read();
    const timer = setInterval(() => void read(), TRACE_POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [kind, id]);

  // The latest reading: the lines since its last start, and its planning: those since the last plan start.
  const lastStart = steps.map((s) => s.step).lastIndexOf('start');
  const round = steps[lastStart]?.facts?.round ?? 1;
  const reading = summed(lastStart < 0 ? steps.filter((s) => s.stage !== 'plan') : steps.slice(lastStart + 1).filter((s) => s.stage !== 'plan'));
  const lastPlan = steps.map((s) => s.step).lastIndexOf('plan_start');
  const planning = summed(lastPlan < 0 ? [] : steps.slice(lastPlan + 1));
  const readingNow = doc?.status === 'decomposing';
  const planningNow = doc?.status === 'planning';
  const readDefs = round > 1 ? READ : READ.filter((d) => d.step !== 'answers');

  return (
    <div className="space-y-3">
      {kind === 'document' && (
        <>
          <Group title={`Read · round ${round}`}>
            <Column
              defs={readDefs}
              facts={reading}
              running={readingNow}
              grey={(step) => (round > 1 && ROUND_ONE_ONLY.has(step) ? 'round 1 only' : step === 'critic' && !readingNow && !reading.has('critic') ? 'no critic round' : null)}
            />
            {doc !== null && doc.status !== 'decomposing' && doc.can_answer && (
              <p className="mt-1.5 rounded border border-dashed border-muted-foreground/40 px-2 py-1 text-[11px] text-muted-foreground">↺ the answers start the next round</p>
            )}
          </Group>
          {(planning.size > 0 || planningNow) && (
            <Group title="Plan">
              <Column defs={PLAN} facts={planning} running={planningNow} />
            </Group>
          )}
        </>
      )}
      {plan !== null && (
        <>
          {kind === 'document' && <Arrow dashed />}
          <Group title={plan.origin === 'revert' ? 'Revert' : plan.run?.kind === 'dry_run' ? 'Run · dry run' : 'Run'}>
            <RunColumn plan={plan} />
          </Group>
          {plan.revert && (
            <Group title="Revert">
              <div>
                {plan.revert.steps.map((s, i) => (
                  <div key={s.step_key}>
                    {i > 0 && <Arrow />}
                    <Box label={s.message} state={s.outcome === 'reverted' ? 'done' : 'grey'} note={s.outcome} />
                  </div>
                ))}
              </div>
            </Group>
          )}
        </>
      )}
      {kind === 'document' && steps.length === 0 && <p className="text-xs text-muted-foreground">No steps recorded for it in the last day.</p>}
    </div>
  );
}
